import { supabase } from './supabaseClient';
import { getConfig, getEnvironment } from '../env';

const config = getConfig();
const apiBase = typeof window !== 'undefined' && getEnvironment() !== 'development'
  ? window.location.origin
  : config.apiBase;

// Typed client for the real trusted-organization intelligence bridge
// (server.js's `/api/intelligence/*`, backed by lib/intelligencebridge.js).
//
// This is a thin, dedicated fetch rather than a call through the shared
// apiClient: the bridge's failure responses carry a specific, meaningful
// `code` (BRIDGE_DISABLED, BRIDGE_MISCONFIGURED, BRIDGE_UNAVAILABLE, ...)
// and a human-readable `error` message that the UI needs to show verbatim
// (same principle as Code's `capabilities().execution.reason`) -- the
// shared client's HTTP-status error taxonomy collapses all of these into a
// generic "temporarily unavailable" message, which would hide the real
// reason from the user.

export interface IntelligenceBridgeFailure {
  ok: false;
  error: string;
  code: string;
}

export interface IntelligenceBridgeSuccess<T> {
  ok: true;
  data: T;
}

export type IntelligenceBridgeResult<T> = IntelligenceBridgeSuccess<T> | IntelligenceBridgeFailure;

async function callIntelligence<T>(path: string): Promise<IntelligenceBridgeResult<T>> {
  let token: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token ?? null;
  } catch {
    token = null;
  }

  let response: Response;
  try {
    response = await fetch(`${apiBase}${path}`, {
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    return { ok: false, error: 'Could not reach the OZIRA service.', code: 'NETWORK' };
  }

  const payload = await response.json().catch(() => null) as (Partial<IntelligenceBridgeFailure> & Record<string, unknown>) | null;

  if (!response.ok) {
    return {
      ok: false,
      error: (payload && typeof payload.error === 'string' && payload.error) || 'The shared intelligence service is not available right now.',
      code: (payload && typeof payload.code === 'string' && payload.code) || 'UNKNOWN',
    };
  }
  return { ok: true, data: (payload ?? {}) as T };
}

export function getIntelligenceWhoami(): Promise<IntelligenceBridgeResult<Record<string, unknown>>> {
  return callIntelligence('/api/intelligence/whoami');
}

export function getIntelligenceSignals(): Promise<IntelligenceBridgeResult<Record<string, unknown>>> {
  return callIntelligence('/api/intelligence/signals');
}
